import { FC, useEffect, useRef } from "react";
import { JuspaySdkPayload } from "@onmo/graphql/graphql";
import {
  loadJuspaySDK,
  HyperServicesInstance,
  routeJuspayStatus,
  handleSdkEvent,
  createNativeHyperAdapter,
  buildInitPayload,
} from "../../utils/juspaySDK";
import { isAndroidApp } from "../../utils/platform";
import { useStyles } from "./Payment.style";
import clsx from "classnames";
import { isMobile } from "../../constants/dimension";
import { HYPER_SDK_DIV_ID } from "../../constants/juspay";

export interface IJuspayCheckoutProps {
  sdkPayload: JuspaySdkPayload;
  orderId: string;
  onVerify: (orderId: string) => void;
  onFailure: (data: Record<string, unknown>) => void;
  onCancel: () => void;
  onReady?: () => void;
}

const JuspayCheckout: FC<IJuspayCheckoutProps> = (props) => {
  const { sdkPayload, orderId, onVerify, onFailure, onCancel, onReady } = props;
  const readyRef = useRef(false);
  const classes = useStyles();
  const hyperRef = useRef<HyperServicesInstance | null>(null);

  useEffect(() => {
    let mounted = true;
    const callbacks = { onVerify, onFailure, onCancel };

    const init = async () => {
      try {
        let hyper: HyperServicesInstance;

        if (isAndroidApp) {
          hyper = createNativeHyperAdapter();
        } else {
          await loadJuspaySDK();
          if (!mounted) return;
          hyper = new window.HyperServices();
        }

        hyperRef.current = hyper;

        hyper.initiate(
          buildInitPayload(sdkPayload, isAndroidApp),
          (eventData: string | Record<string, unknown>) => {
            if (!mounted) return;
            if (!readyRef.current) {
              readyRef.current = true;
              onReady?.();
            }
            handleSdkEvent(eventData, hyper, sdkPayload, callbacks, orderId);
          },
        );
      } catch (err) {
        if (!mounted) return;
        console.error("[JuspayCheckout] init failed:", err);
        onFailure({ error: "SDK failed to load. Please try again." });
      }
    };

    init();

    return () => {
      mounted = false;
      hyperRef.current?.terminate?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (e.data?.type !== "JUSPAY_REDIRECT_RESULT") return;

      const { orderId: redirectOrderId, status, errorCode } = e.data;
      routeJuspayStatus(
        status,
        errorCode,
        { status, orderId: redirectOrderId, errorCode },
        redirectOrderId,
        { onVerify, onFailure, onCancel },
      );
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onVerify, onFailure, onCancel]);

  if (isAndroidApp) {
    return null;
  }

  return (
    <div
      id={HYPER_SDK_DIV_ID}
      className={clsx(classes.juspayCheckout, {
        space: !isMobile,
      })}
    ></div>
  );
};

export default JuspayCheckout;
